import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';

interface TopologyCanvasConfigurationFormProps {
  config: any;
  onChange: (config: any) => void;
}

interface TopologyObject {
  id: string;
  type: string;
  position: 'dedans' | 'dehors';
  color: string;
  emoji: string;
}

interface Zone {
  id: string;
  type: 'circle' | 'square' | 'triangle';
  x: number;
  y: number;
  radius: number;
  color: string;
  label: string;
}

const emojiSuggestions = [
  '⚽', '🏀', '🎾', '🏈', '⚾', '🎯', '🎲', '🧸', '🐻', '🐰',
  '🐱', '🐶', '🐭', '🐹', '🐨', '🐼', '🦊', '🐸', '🐯', '🦁',
  '🍎', '🍊', '🍌', '🍇', '🍓', '🍑', '🥝', '🍅', '🥕', '🌽',
  '📚', '📖', '📝', '✏️', '🖊️', '🖍️', '📏', '📐', '✂️', '📌',
  '🚗', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑', '🚒', '🚐', '🛻',
  '🏠', '🏡', '🏢', '🏣', '🏤', '🏥', '🏦', '🏨', '🏩', '🏪'
];

const colorOptions = [
  { value: 'red', label: 'Rouge', color: '#EF4444' },
  { value: 'blue', label: 'Bleu', color: '#3B82F6' },
  { value: 'green', label: 'Vert', color: '#10B981' },
  { value: 'yellow', label: 'Jaune', color: '#F59E0B' },
  { value: 'purple', label: 'Violet', color: '#8B5CF6' },
  { value: 'orange', label: 'Orange', color: '#F97316' },
  { value: 'pink', label: 'Rose', color: '#EC4899' },
  { value: 'brown', label: 'Marron', color: '#A3A3A3' }
];

const zoneTypes = [
  { value: 'circle', label: 'Cercle', icon: '⭕' },
  { value: 'square', label: 'Carré', icon: '⬜' },
  { value: 'triangle', label: 'Triangle', icon: '🔺' }
];

export const TopologyCanvasConfigurationForm: React.FC<TopologyCanvasConfigurationFormProps> = ({
  config,
  onChange
}) => {
  const [question, setQuestion] = useState(config.question || 'Placez les objets dedans ou dehors du cercle');
  const [objects, setObjects] = useState<TopologyObject[]>(config.objects || []);
  const [zones, setZones] = useState<Zone[]>(config.zones || []);
  const [allowInteraction, setAllowInteraction] = useState(config.allowInteraction !== false);

  // Initialize default zone if none exists
  useEffect(() => {
    if (zones.length === 0) {
      const defaultZone: Zone = {
        id: 'zone1',
        type: 'circle',
        x: 300,
        y: 200,
        radius: 120,
        color: '#3B82F6',
        label: 'Zone délimitée'
      };
      setZones([defaultZone]);
    }
  }, [zones.length]);

  // Update config when any field changes
  useEffect(() => {
    onChange({
      question,
      objects,
      zones,
      allowInteraction
    });
  }, [question, objects, zones, allowInteraction, onChange]);

  const addObject = () => {
    const newObject: TopologyObject = {
      id: `object_${Date.now()}`,
      type: 'circle',
      position: 'dedans',
      color: 'red',
      emoji: '⚽'
    };
    setObjects([...objects, newObject]);
  };

  const removeObject = (index: number) => {
    setObjects(objects.filter((_, i) => i !== index));
  };

  const updateObject = (index: number, field: keyof TopologyObject, value: any) => {
    const updatedObjects = objects.map((obj, i) => 
      i === index ? { ...obj, [field]: value } : obj
    );
    setObjects(updatedObjects);
  };

  const addZone = () => {
    const newZone: Zone = {
      id: `zone_${Date.now()}`,
      type: 'circle',
      x: 300,
      y: 200,
      radius: 120,
      color: '#3B82F6',
      label: 'Nouvelle zone'
    };
    setZones([...zones, newZone]);
  };

  const removeZone = (index: number) => {
    setZones(zones.filter((_, i) => i !== index));
  };

  const updateZone = (index: number, field: keyof Zone, value: any) => {
    const updatedZones = zones.map((zone, i) => 
      i === index ? { ...zone, [field]: value } : zone
    );
    setZones(updatedZones);
  };

  return (
    <div className="space-y-6">
      {/* Question Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question ou consigne
        </label>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          placeholder="Placez les objets dedans ou dehors du cercle"
        />
      </div>

      {/* Zones Configuration */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Zones délimitées
          </label>
          <button
            onClick={addZone}
            className="flex items-center gap-2 px-3 py-1 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter une zone
          </button>
        </div>
        
        <div className="space-y-3">
          {zones.map((zone, index) => (
            <div key={zone.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-700">Zone {index + 1}</h4>
                <button
                  onClick={() => removeZone(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Type</label>
                  <select
                    value={zone.type}
                    onChange={(e) => updateZone(index, 'type', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  >
                    {zoneTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Couleur</label>
                  <div className="flex gap-1">
                    {colorOptions.map(color => (
                      <button
                        key={color.value}
                        onClick={() => updateZone(index, 'color', color.color)}
                        className={`w-6 h-6 rounded border-2 ${
                          zone.color === color.color ? 'border-gray-800' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.color }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Position X</label>
                  <input
                    type="number"
                    value={zone.x}
                    onChange={(e) => updateZone(index, 'x', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    min="0"
                    max="600"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Position Y</label>
                  <input
                    type="number"
                    value={zone.y}
                    onChange={(e) => updateZone(index, 'y', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    min="0"
                    max="400"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Taille</label>
                  <input
                    type="number"
                    value={zone.radius}
                    onChange={(e) => updateZone(index, 'radius', parseInt(e.target.value))}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    min="50"
                    max="200"
                  />
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Label</label>
                  <input
                    type="text"
                    value={zone.label}
                    onChange={(e) => updateZone(index, 'label', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="Zone délimitée"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Objects Configuration */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            Objets à placer
          </label>
          <button
            onClick={addObject}
            className="flex items-center gap-2 px-3 py-1 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter un objet
          </button>
        </div>
        
        <div className="space-y-3">
          {objects.map((obj, index) => (
            <div key={obj.id} className="p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-700">Objet {index + 1}</h4>
                <button
                  onClick={() => removeObject(index)}
                  className="text-red-500 hover:text-red-700"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Emoji</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={obj.emoji}
                      onChange={(e) => updateObject(index, 'emoji', e.target.value)}
                      className="w-16 p-2 border border-gray-300 rounded text-center text-lg"
                      maxLength={2}
                    />
                    <div className="flex-1">
                      <select
                        value={obj.emoji}
                        onChange={(e) => updateObject(index, 'emoji', e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded text-sm"
                      >
                        <option value="">Choisir un emoji</option>
                        {emojiSuggestions.map(emoji => (
                          <option key={emoji} value={emoji}>{emoji}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Position correcte</label>
                  <select
                    value={obj.position}
                    onChange={(e) => updateObject(index, 'position', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="dedans">Dedans</option>
                    <option value="dehors">Dehors</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Couleur</label>
                  <div className="flex gap-1">
                    {colorOptions.map(color => (
                      <button
                        key={color.value}
                        onClick={() => updateObject(index, 'color', color.value)}
                        className={`w-6 h-6 rounded border-2 ${
                          obj.color === color.value ? 'border-gray-800' : 'border-gray-300'
                        }`}
                        style={{ backgroundColor: color.color }}
                        title={color.label}
                      />
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-xs text-gray-600 mb-1">Type</label>
                  <input
                    type="text"
                    value={obj.type}
                    onChange={(e) => updateObject(index, 'type', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="circle, square, etc."
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Settings */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Paramètres
        </label>
        <div className="space-y-2">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={allowInteraction}
              onChange={(e) => setAllowInteraction(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">Permettre l'interaction</span>
          </label>
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-700 mb-2">Résumé de la configuration</h4>
        <div className="text-sm text-gray-600 space-y-1">
          <p>• {zones.length} zone(s) délimitée(s)</p>
          <p>• {objects.length} objet(s) à placer</p>
          <p>• {objects.filter(obj => obj.position === 'dedans').length} objet(s) à placer dedans</p>
          <p>• {objects.filter(obj => obj.position === 'dehors').length} objet(s) à placer dehors</p>
        </div>
      </div>
    </div>
  );
};
